import { Autocomplete, Stack, TextField } from '@mui/material';
import { getSelectorAttribute, createPlanPageSelectors } from '@vy/app-selectors';
import type { ObjectId, UsageProductVariantInput } from '@vy/types';
import type { Nullable } from '@vy/utils';
import { isNotNullOrUndefined, purge, withDefault } from '@vy/utils';
import { flatMap, get } from 'lodash';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFormErrorNotificationContext } from '../../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useMeters } from '../../../../../../../../hooks/requests/use-meters/use-meters';

import { creditTypeIdsFromSource } from '../../../../shared/credit-type-ids-from-source';
import { useUsagePricingOption } from '../../../../../create-plan-page-hooks/use-usage-pricing-option';
import type { NewPlanForm } from '../../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { ProductSourceSelect } from '../../ProductSourceSelect/ProductSourceSelect';
import {
  getAllowedUsagePricingOptions,
  getUsagePricingModelValue,
} from '../../../../../create-plan-page-services/usage-pricing-model-value-provider';

type PerUnitUsageChargeEditProps = {
  productIndex: number;
  productVariant: UsageProductVariantInput;
  productVariantIndex: number;
};

export type SelectedProductNameType = {
  isPreviousMeterName: boolean;
  value: string;
};

export const PerUnitUsageChargeForm: React.FC<PerUnitUsageChargeEditProps> = ({
  productIndex,
  productVariantIndex,
  productVariant
}) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    register,
    control,
    getFieldState,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<PlanInputForm, NewPlanForm>();
  const [selectedProductName, setSelectedProductName] = useState<SelectedProductNameType>({
    isPreviousMeterName: false,
    value: '',
  });
  const {
    data: { meters },
  } = useMeters();
  const onUsagePricingOptionChange = useUsagePricingOption(productIndex, productVariantIndex);
  const usageProducts = watch('usageProducts');
  const selectedMetersIds = purge(
    flatMap(usageProducts, ({ variants }) => variants.map(({ meterId }) => meterId)),
  );

  const nameRegister = register(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`);

  const pricePerUnitRegister = register(
    `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.price`,
    {
      valueAsNumber: true,
    },
  );

  registerErrorField(nameRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(pricePerUnitRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(
    `usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`,
    CREATE_PLAN_TABS.tierAndVolumePricing,
  );

  function handleMeterSelect(newMeterId: Nullable<ObjectId>) {
    const meter = meters.find(({ id }) => id === newMeterId);
    setValue(`usageProducts.${productIndex}.creditTypeIds`, creditTypeIdsFromSource(meter));
    if (selectedProductName.isPreviousMeterName || selectedProductName.value === '') {
      const meterName = withDefault(meter?.name, '');
      setSelectedProductName({ isPreviousMeterName: true, value: meterName });
      setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`, meterName);
    }
  }

  function handleNameChange(value: string) {
    setSelectedProductName({ isPreviousMeterName: false, value });
    setValue(nameRegister.name, value);
  }
  
  const allowedUsagePricingOptions = getAllowedUsagePricingOptions(
    usageProducts?.[productIndex]?.variants,
    productVariantIndex,
  );

  return (
    <Stack direction="row" sx={{
      columnGap: 2
    }}>
      <Autocomplete
        fullWidth
        autoHighlight
        options={allowedUsagePricingOptions}
        renderInput={(params) => <TextField {...params} label="Select pricing" placeholder="Pricing" />}
        value={getUsagePricingModelValue(productVariant)}
        onChange={onUsagePricingOptionChange}
      />
      <Controller
        control={control}
        name={`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`}
        render={({ field }) => (
          <ProductSourceSelect
            error={isNotNullOrUndefined(
              get(errors, `usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`),
            )}
            helperText={
              getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`).error
                ?.message
            }
            value={field.value}
            selectedMetersIds={selectedMetersIds}
            onChange={(event, newValue) => {
              event.preventDefault();
              handleMeterSelect(newValue);
              field.onChange(newValue);
            }}
          />
        )}
      />
      <TextField
        {...getSelectorAttribute(createPlanPageSelectors.products.usage.perUnit.nameInput)}
        label="Product name"
        size="medium"
        fullWidth
        {...nameRegister}
        onChange={(event) => handleNameChange(event.currentTarget.value)}
        helperText={get(errors, nameRegister.name.toString())?.message}
        error={isNotNullOrUndefined(get(errors, nameRegister.name.toString()))}
      />
      <TextField
        {...getSelectorAttribute(createPlanPageSelectors.products.usage.perUnit.priceInput)}
        size="medium"
        fullWidth
        label="Price"
        prefix="$"
        {...pricePerUnitRegister}
        type="number"
        helperText={get(errors, pricePerUnitRegister.name.toString())?.message}
        error={isNotNullOrUndefined(get(errors, pricePerUnitRegister.name.toString()))}
      />
    </Stack>
  );
};
